import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../utils/index';
import { Z_INDEX } from '../../utils/z-index';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
        nested?: boolean;
    }
>(({ className, nested = false, style, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 bg-black/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        style={{
            ...style,
            zIndex: nested ? Z_INDEX.NESTED_OVERLAY : Z_INDEX.BASE_OVERLAY,
        }}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        showCloseButton?: boolean;
        nested?: boolean;
        preventDismiss?: boolean;
    }
>(({ className, children, showCloseButton = true, nested = false, preventDismiss = true, style, ...props }, ref) => (
    <DialogPortal>
        <DialogOverlay nested={nested} />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                'max-w-[520px] py-8 rounded-xl max-md:rounded-t-xl max-md:rounded-b-none bg-dialog fixed left-1/2 top-1/2 overflow-auto flex flex-col w-[calc(100%-32px)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 shadow-custom-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] border border-border',
                className
            )}
            style={{
                ...style,
                zIndex: nested ? Z_INDEX.NESTED_CONTENT : Z_INDEX.BASE_CONTENT,
            }}
            {...props}
            onInteractOutside={preventDismiss ? e => e.preventDefault() : props.onInteractOutside}
            onEscapeKeyDown={preventDismiss ? e => e.preventDefault() : props.onEscapeKeyDown}
            onCloseAutoFocus={event => {
                event.preventDefault();
                document.body.style.pointerEvents = '';
            }}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close className="absolute right-4 top-4 z-50 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                    <X className="w-6 h-6 text-text" />
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className="bg-dialog shrink-0 h-12 sticky top-0 z-[1000]
    flex items-center justify-center relative
    after:content-[''] after:absolute after:bottom-0 after:left-6 after:right-6
    after:h-px after:bg-border"
    >
        <div className={cn('text-sm font-semibold', className)} {...props} />
        <DialogPrimitive.Close className="absolute right-5 top-3.5 z-50 rounded-md p-0.5 hover:bg-hover transition-colors disabled:pointer-events-none">
            <X className="w-4 h-4 text-text-500" />
        </DialogPrimitive.Close>
    </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className="relative sticky bottom-0 bg-dialog z-20 pb-6">
        <div className="pointer-events-none absolute -top-10 left-0 right-0 h-10 bg-gradient-to-t from-dialog to-transparent z-10" />
        <div className={cn('', className)} {...props} />
    </div>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('text-center font-semibold text-base break-keep', className)}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description ref={ref} className={cn('text-sm text-sub-title', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogTrigger,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
